/*
 * Focus modality — the heuristic behind `:focus-visible`, in JavaScript.
 *
 * CSS can ask `:focus-visible` directly, and everything visual in this library does (the design direction
 * forbids styling plain `:focus`). Behaviour cannot: a component that has to *decide* something on focus —
 * Tooltip opening, for instance — needs the same answer in JavaScript, and `element.matches(':focus-visible')`
 * is not usable for it. jsdom answers `false` for every element without throwing, so a component that trusted
 * it would silently never open under test, and the behaviour would be unassertable.
 *
 * So we track the modality ourselves, with the rules browsers use: the session starts in keyboard modality
 * (a page focused before any interaction shows its ring), a pointer press switches it off, and a key press
 * that is not a pure modifier chord switches it back on. Pointer *movement* is deliberately not a signal —
 * hovering a control is not "using the mouse to focus" — which is what keeps a hover tooltip and a keyboard
 * tooltip independent.
 */

let keyboardModality = true;
let tracking = false;

function onKeyDown(event: KeyboardEvent): void {
  // A chord like ⌘R is the user driving the browser, not moving focus through the page.
  if (event.metaKey || event.altKey || event.ctrlKey) return;
  keyboardModality = true;
}

function onPointerDown(): void {
  keyboardModality = false;
}

/**
 * Starts the document-level modality listeners. Idempotent, and safe to call from any component's mount
 * effect; the listeners are capture-phase and passive in effect, so nothing downstream can suppress them.
 */
export function trackFocusModality(): void {
  if (tracking || typeof document === 'undefined') return;
  tracking = true;
  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('mousedown', onPointerDown, true);
  document.addEventListener('touchstart', onPointerDown, true);
}

/** True when focus arriving now would draw a focus ring — i.e. the user is on the keyboard. */
export function isFocusVisible(): boolean {
  return keyboardModality;
}

/** Test seam: returns to the initial keyboard modality. */
export function resetFocusModality(): void {
  keyboardModality = true;
}
